import React, { useEffect } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Field, FieldArray, useFormikContext, ErrorMessage } from "formik";
import { PricingSectionProps } from "../service-management-props";
import RequestStatus from "../../../../common/components/request-status/request-status";

const PricingSection: React.FC<PricingSectionProps> = ({
  isReadOnly,
  setLoading,
  setAlertMessage,
  setAlertType,
  setAlertOpen,
  serviceDetails,
  setServiceDetails,
  actionType,
}) => {
  const { values, setFieldValue, errors, touched } = useFormikContext<any>();
  const pricing = values.pricingInformation;
  const getPrice = (item: any) => parseFloat(item.price) || 0;
  // Set default payment method to ONLINE if not set
  useEffect(() => {
    if (pricing.acceptedPaymentMethod === undefined || pricing.acceptedPaymentMethod === "") {
      setFieldValue("pricingInformation.acceptedPaymentMethod", "online");
    }
  }, [pricing.acceptedPaymentMethod, setFieldValue]);

  //now create items array combining serviceDetails base service, subServicesDetails and merchandiseDetails
  useEffect(() => {
    // If we're in "view" mode, don't overwrite the items from Formik initialValues.
    // Re-calculating here is only necessary when a user is interacting with tab 0
    // during "create" or "edit" mode.
    if (actionType === "view") return;

    //get subServices details from serviceDetails using subServiceNames in values.serviceInformation.subServiceIds
    const subServicesDetails = serviceDetails?.subServices?.filter((subService: any) =>
      values?.serviceInformation?.subServiceIds.includes(subService?.name)
    ) || [];
    //get merchandise details from serviceDetails using merchandiseIds in values.serviceInformation.merchandiseIds
    const merchandiseDetails = serviceDetails?.merchandises?.filter((merchandise: any) =>
      values.serviceInformation.merchandiseIds.includes(merchandise?.name)
    ) || [];

    const combinedItems = [
      {
        id: serviceDetails?.id,
        name: serviceDetails?.name,
        type: "Service",
        minPrice: serviceDetails?.minPrice,
        maxPrice: serviceDetails?.maxPrice,
        // Use providerPrice from serviceDetails first (avoids Formik reInit timing issue),
        // then fall back to any value the user may have typed.
        selected: values?.pricingInformation?.items?.find((item: any) => (item.id === serviceDetails?.id && item.type === "Service"))?.selected
          ?? (serviceDetails?.providerPrice != null ? true : false),
        price: values?.pricingInformation?.items?.find((item: any) => (item.id === serviceDetails?.id && item.type === "Service"))?.price
          ?? serviceDetails?.providerPrice
          ?? "",
      },
      ...subServicesDetails?.map((subService: any) => {
                const existingSub = values?.pricingInformation?.items?.find(
            (item: any) =>
              item.id === subService?.id && item.type === "Sub-service",
          );
        return {
          id: subService?.id,
          name: subService?.name,
          type: "Sub-service",
          minPrice: subService?.minPrice,
          maxPrice: subService?.maxPrice,
         selected:
  values?.pricingInformation?.items?.find(
    (item: any) =>
      item.id === subService?.id &&
      item.type === "Sub-service"
  )?.selected ??
  false,
          price:
  actionType === "edit"
    ? (existingSub?.price ?? "")
    : (existingSub?.price ?? subService?.providerPrice ?? ""),
        };
        
      }),
      
      ...merchandiseDetails?.map((merchandise: any) => {
        const existingMerch = values?.pricingInformation?.items?.find(
          (item: any) =>
            item.id === merchandise?.id && item.type === "Merchandise",
        );
        return {
          id: merchandise?.id,
          name: merchandise?.name,
          type: "Merchandise",
          minPrice: merchandise?.minPrice,
          maxPrice: merchandise?.maxPrice,
          selected:
            actionType === "edit"
              ? (existingMerch?.selected ?? false)
              : values?.pricingInformation?.items?.find((item: any) => (item.id === merchandise?.id && item.type === "Merchandise"))?.selected,
         price:
  actionType === "edit"
    ? (existingMerch?.price ?? "")
    : (existingMerch?.price ?? merchandise?.providerPrice ?? ""),
        };
      }),
    ];
    setFieldValue("pricingInformation.items", combinedItems);
  }, [serviceDetails, values.serviceInformation?.subServiceIds, values.serviceInformation?.merchandiseIds, actionType]);

  const getType = (type: string) => {
    switch (type) {
      case "Service":
        return "#176C73";
      case "Sub-service":
        return "#6B7280";
      case "Merchandise":
        return "#D97706";
      default:
        return "black";
    }
  };

  useEffect(() => {
    if (!pricing?.items || pricing.items.length === 0) return;
    const mainIndex = pricing.items.findIndex((item: any) => item.type === 'Service');
    if (mainIndex === -1) return;
    const mainChecked = pricing.items[mainIndex].selected;
    const onlySubOrMerchChecked = !mainChecked && pricing.items.some((item: any) => item.selected && item.type !== 'Service');
    if (onlySubOrMerchChecked) {
      const sumSelected = pricing.items.reduce((sum: number, item: any) => {
        if (item.selected && item.type !== 'Service') {
          const price = parseFloat(item.price) || 0;
          return sum + price;
        }
        return sum;
      }, 0);
      if (pricing.items[mainIndex].price !== sumSelected) {
        const updated = [...pricing.items];
        updated[mainIndex] = { ...updated[mainIndex], price: sumSelected };
        setFieldValue('pricingInformation.items', updated, false);
      }
    }

  }, [pricing.items, setFieldValue]);

  const ErrorText = ({ name }: { name: string }) => (
    <ErrorMessage name={name} component="div" className="error-message" />
  );

  return (
    <div className="pricing-section service-form-card">
      <Box className="selected-service-details">
        <div className="selected-item-details">
          <h5>Category:</h5>
          <div className="selected-item-label">{serviceDetails?.category}</div>
        </div>
        <div className="selected-item-details">
          <h5>Service Name:</h5>
          <div className="selected-item-label">{serviceDetails?.name}</div>
        </div>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "12px",
        }}
      >
        <h3 style={{ margin: 0 }}>
          Pricing Details<span className="required-asterisk">*</span>
        </h3>
        <Typography
          sx={{
            color: "#6B6B6B",
            fontSize: "12px",
            lineHeight: "32px",
            fontWeight: 400,
          }}
        >
          *Only Immediate Need services are billed. For Preneed services,
          coordinate payment with the customer
        </Typography>
      </Box>
      <div className="pricing-table-container">
        <FieldArray name="pricingInformation.items">
          {() => (
            <>
              <Table className="pricing-table">
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>Item Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Allowed Range ($)</TableCell>
                    <TableCell>Enter Price ($)</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {(() => {
                    // Find main service index
                    const mainIndex = pricing.items.findIndex((item: any) => item.type === 'Service');
                    const mainChecked = mainIndex !== -1 && pricing.items[mainIndex].selected;
                    // If main is checked, all others are checked and only main price is editable
                    // If only sub/merchandise are checked, only their price is editable, main price is sum of their min prices
                    const handleCheckboxChange = (e: any, index: number) => {
                      const updated = [...pricing.items];
                      if (updated[index].type === "Service") {
                        if (actionType === "edit") {
                          return;
                        }
                        // Main service toggled
                        const checked = e.target.checked;
                        updated[index].selected = checked;
                        // Preselect and disable all sub-services if main is checked
                        for (let i = 0; i < updated.length; i++) {
                          if (i !== index &&updated[i].type === "Sub-service") {
                            if (actionType !== "edit") {
                              updated[i].selected = checked;
                            }
                            updated[i].__forceDisabled = checked; // custom flag for disabling
                            if (checked) {
                              updated[i].price = "";
                            }
                          } else if (i !== index) {
                            // For non sub-services, just set selected
                            updated[i].selected = checked;
                            updated[i].__forceDisabled = false;
                            if (checked) {
                              updated[i].price = "";
                            }
                          }
                        }
                        setFieldValue("pricingInformation.items", updated);
                      } else {
                      const updated = [...pricing.items];

                      const item = updated[index];
                      const mainIndex = updated.findIndex(i => i.type === "Service");

                      const main = updated[mainIndex];

                      const itemPrice = getPrice(item);
                      let mainPrice = getPrice(main);

                      const wasSelected = item.selected;
                      const nowSelected = e.target.checked;

                      item.selected = nowSelected;

                      if (!wasSelected && nowSelected) {
                        mainPrice += itemPrice;
                      }
                      if (wasSelected && !nowSelected) {
                        mainPrice -= itemPrice;
                      }
                      main.price = mainPrice;

                      setFieldValue("pricingInformation.items", updated);
                    }
                    };
                    // Calculate if only sub/merchandise are checked
                    const onlySubOrMerchChecked = !mainChecked && pricing.items.some((item: any) => item.selected && item.type !== 'Service');
                    // Calculate sum of selected sub/merch entered prices
                    let sumSelected = 0;
                    if (onlySubOrMerchChecked) {
                      sumSelected = pricing.items.reduce((sum: number, item: any) => {
                        if (item.selected && item.type !== 'Service') {
                          const price = parseFloat(item.price) || 0;
                          return sum + price;
                        }
                        return sum;
                      }, 0);
                    }
                    return pricing?.items?.map((item: any, index: number) => {
                      const checked = !!item.selected;
                       let priceDisabled = isReadOnly;
                      if (actionType === "edit") {
                       if (item.type === "Service") {
                          priceDisabled = false;
                        }  else {
                          priceDisabled = isReadOnly || !item.selected;
                        }
                      } else {
                        if (mainChecked) {
                          priceDisabled = isReadOnly || item.type !== "Service";
                        } else if (onlySubOrMerchChecked) {
                          if (item.type === "Service") priceDisabled = true;
                          else priceDisabled = isReadOnly || !item.selected;
                        } else {
                          priceDisabled = isReadOnly || !item.selected;
                        }
                      }
                      // If only sub/merchandise checked, set main price to sum
                      let priceValue = item.price;
                      if (item.type === 'Service' && onlySubOrMerchChecked) {
                        priceValue = sumSelected;

                      }
                      return (
                        <TableRow key={item?.id} className={`${item?.type}-row`}>
                          <TableCell>
                            {item?.type !== "Service" && (
                              <input
                                type="checkbox"
                                checked={!!item.selected}
                                onChange={(e) => handleCheckboxChange(e, index)}
                                className="service-select-checkbox"
                                name={`pricingInformation.items.${index}.selected`}
                                disabled={isReadOnly}
                              />
                            )}
                          </TableCell>
                          <TableCell>{item?.name}</TableCell>
                          <TableCell>
                            <RequestStatus status={item?.type} />
                          </TableCell>
                          <TableCell
                            className="allowed-price-range"
                            style={{ color: getType(item?.type) }}
                          >
                            ${item?.minPrice} - ${item?.maxPrice}
                          </TableCell>
                          <TableCell>
                            <div className="form-field price-input-field">
                              <Field
                                name={`pricingInformation.items[${index}].price`}
                                as={TextField}
                                placeholder="Enter price"
                                type="number"
                                disabled={priceDisabled}
                                inputProps={{
                                  min: item?.minPrice ?? 0,
                                  max: item?.maxPrice ?? undefined,
                                }}
                                value={priceValue}
                                onWheel={(e) =>
                                  e.target instanceof HTMLElement && e.target.blur()
                                }
                                onChange={(e: any) => {
                                const updated = [...pricing.items];
                                const item = updated[index];
                                const mainIndex = updated.findIndex(i => i.type === "Service");
                                const main = updated[mainIndex];
                                const oldPrice = getPrice(item);
                                const newPrice = parseFloat(e.target.value) || 0;
                                item.price = e.target.value;
                                if (item.type !== "Service" && item.selected) {
                                  main.price =
                                    getPrice(main) - oldPrice + newPrice;
                                }
                                setFieldValue("pricingInformation.items", updated);
                              }}
                              />
                              <ErrorText
                                name={`pricingInformation.items[${index}].price`}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
              {(() => {
                // Calculate mainChecked outside JSX for safety
                const mainIndex = pricing?.items?.findIndex((item: any) => item.type === 'Service');
                const mainChecked = mainIndex !== -1 && pricing?.items[mainIndex]?.selected;
                let total = 0;
                if (mainChecked) {
                  const main = pricing.items[mainIndex];
                  total = main && main?.selected ? (parseFloat(main?.price) || 0) : 0;
                } else {
                  total = pricing.items.reduce((sum: number, item: any) => {
                    if (item?.selected && item.type !== 'Service') {
                      const price = parseFloat(item?.price) || 0;
                      return sum + price;
                    }
                    return sum;
                  }, 0);
                }
                return (
                  <div className="total-price-display">
                    Total Price: ${total}
                  </div>
                );
              })()}
            </>
          )}
        </FieldArray>
      </div>
      {/* Additional Pricing */}
      <div className="additional-pricing">
        <p className="additional-fees-text">Additional Pricing Details</p>
        <div className="provider-service-form-grid-three-columns">
          <div className="form-field">
            <label>Insurance Support</label>
            <RadioGroup
              row
              value={pricing?.insuranceSupport ? "yes" : "no"}
              onChange={(e) =>
                setFieldValue(
                  "pricingInformation.insuranceSupport",
                  e.target.value === "yes",
                )
              }
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" disabled={isReadOnly} className="label-price" />
              <FormControlLabel value="no" control={<Radio />} label="No" disabled={isReadOnly} className="label-price" />
            </RadioGroup>
          </div>

          {/* <div className="form-field">
            <label>Additional Fee ($)</label>
            <Field
              name={"pricingInformation.additionalFee"}
              placeholder="Add additional fee"
              value={pricing.additionalFee}
              disabled={isReadOnly}
              type="number"
            />
            <ErrorText name="pricingInformation.additionalFee" />
          </div> */}

          <div className="form-field">
            <label>Accepted Payment Method</label>
            <RadioGroup
              row
              value={pricing.acceptedPaymentMethod}
              onChange={(e) =>
                setFieldValue("pricingInformation.acceptedPaymentMethod", e.target.value)
              }
            >
              <FormControlLabel value="online" control={<Radio />} label="Online" disabled={isReadOnly} className="label-price" />
              <FormControlLabel value="cash" control={<Radio />} label="Cash(coming soon)" disabled={true} className="label-price" />
              {/* <FormControlLabel value="both" control={<Radio />} label="Both" disabled={true} /> */}
            </RadioGroup>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingSection;
